import React, { useState, useEffect } from 'react';
import { Flag, Loader2 } from 'lucide-react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import { Button } from './ui/button';
import { Textarea } from './ui/textarea';
import { Label } from './ui/label';
import api from '../lib/api';
import { toast } from 'sonner';

// Keep in sync with backend/routes/reports.py → REASONS
// CSAE is FIRST — Google Play compliance + immediate email to Child-Safety POC.
const REASONS = [
  { id: 'csae', label: 'Child Sexual Abuse (CSAE / CSAM)', critical: true },
  { id: 'harassment', label: 'Harassment / Bullying' },
  { id: 'sexual_content', label: 'Sexual content / nudity' },
  { id: 'hate_speech', label: 'Hate speech' },
  { id: 'spam', label: 'Spam' },
  { id: 'fraud', label: 'Fraud / Scam' },
  { id: 'impersonation', label: 'Impersonation' },
  { id: 'inappropriate', label: 'Inappropriate content' },
  { id: 'self_harm', label: 'Self-harm / Dangerous content' },
  { id: 'ai_content', label: 'Harmful AI-generated content' },
  { id: 'other', label: 'Other' },
];

/**
 * Generic report dialog. Mounted wherever users see other users OR content.
 *
 * Props:
 *   open / onOpenChange — dialog state
 *   target              — { user_id, displayName } (required)
 *   context             — optional { planet_id?, message_id? } — planet chat context
 *   content             — optional { type: 'planet_message'|'dm'|'feed_post'|'user',
 *                                    id?, preview? } — what specific artifact
 *                          the reporter was looking at. Sent to the backend so the
 *                          admin queue can jump directly to it.
 */
export default function ReportUserDialog({ open, onOpenChange, target, context, content }) {
  const [reason, setReason] = useState('');
  const [details, setDetails] = useState('');
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!open) { setReason(''); setDetails(''); setBusy(false); }
  }, [open]);

  const submit = async () => {
    if (!reason || !target?.user_id) return;
    setBusy(true);
    try {
      await api.post('/reports', {
        target_user_id: target.user_id,
        reason,
        details: details.trim() || undefined,
        planet_id: context?.planet_id,
        message_id: context?.message_id,
        content_type: content?.type,
        content_id: content?.id,
        content_preview: content?.preview,
      });
      toast.success(
        reason === 'csae'
          ? 'Thank you — this has been flagged as critical and forwarded to our Child-Safety team immediately.'
          : 'Report submitted — our moderation team will review it.'
      );
      onOpenChange(false);
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error(typeof detail === 'string' ? detail : 'Could not submit report');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="bg-[#161622] border-white/10 text-white max-w-md" data-testid="report-user-dialog">
        <DialogHeader>
          <DialogTitle className="font-display flex items-center gap-2">
            <Flag size={16} className="text-rose-400" /> {content?.type && content.type !== 'user' ? 'Report content' : 'Report user'}
          </DialogTitle>
          <DialogDescription className="text-white/55">
            {target?.displayName ? <>You&apos;re reporting <span className="text-white font-semibold">{target.displayName}</span>. </> : null}
            Our moderation team reviews every report. Submitting a false report may result in action against your account.
          </DialogDescription>
        </DialogHeader>

        {content?.preview && (
          <div className="rounded-xl border border-white/10 bg-black/25 p-3 text-xs text-white/75" data-testid="report-content-preview">
            <div className="text-[10px] uppercase tracking-wider text-white/40 mb-1">Reported {content.type?.replace('_', ' ') || 'content'}</div>
            <div className="line-clamp-3 whitespace-pre-wrap">{content.preview}</div>
          </div>
        )}

        <div className="space-y-3">
          <Label className="text-xs uppercase tracking-wider text-white/50">Reason</Label>
          <div className="grid gap-1.5" role="radiogroup" aria-label="Report reason">
            {REASONS.map(r => {
              const active = reason === r.id;
              const isCritical = r.critical;
              return (
                <button
                  key={r.id}
                  type="button"
                  role="radio"
                  aria-checked={active}
                  onClick={() => setReason(r.id)}
                  data-testid={`report-reason-${r.id}`}
                  className={`text-left text-sm rounded-xl px-3 py-2 border transition flex items-center gap-2.5 ${
                    active
                      ? (isCritical
                          ? 'bg-red-500/15 border-red-400/60 text-white'
                          : 'bg-rose-500/10 border-rose-400/50 text-white')
                      : (isCritical
                          ? 'bg-red-500/[0.04] border-red-400/25 hover:bg-red-500/[0.08] text-white/90'
                          : 'bg-white/3 border-white/10 hover:bg-white/8 text-white/80')
                  }`}
                >
                  <span
                    className={`shrink-0 w-3.5 h-3.5 rounded-full border ${
                      active
                        ? (isCritical ? 'border-red-400 bg-red-400' : 'border-rose-400 bg-rose-400')
                        : 'border-white/30'
                    }`}
                  />
                  <span className="flex-1">{r.label}</span>
                  {isCritical && (
                    <span className="text-[9px] uppercase tracking-widest text-red-300 font-semibold">Critical</span>
                  )}
                </button>
              );
            })}
          </div>

          <div>
            <Label htmlFor="report-details" className="text-xs uppercase tracking-wider text-white/50">
              Additional details <span className="lowercase text-white/30">(optional)</span>
            </Label>
            <Textarea
              id="report-details"
              value={details}
              onChange={(e) => setDetails(e.target.value)}
              placeholder="What happened? Add context, links, or what was said…"
              maxLength={1000}
              data-testid="report-details"
              className="mt-1.5 bg-white/5 border-white/10 text-white min-h-[90px]"
            />
            <div className="text-[10px] text-white/35 text-right mt-1">{details.length}/1000</div>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => onOpenChange(false)}
            className="border-white/15 bg-white/5 hover:bg-white/10 text-white"
          >
            Cancel
          </Button>
          <Button
            onClick={submit}
            disabled={!reason || busy}
            data-testid="report-submit"
            className="bg-gradient-to-r from-rose-500 to-pink-500 text-white border-0 disabled:opacity-40"
          >
            {busy ? <Loader2 size={14} className="animate-spin mr-2" /> : <Flag size={14} className="mr-2" />}
            Submit report
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
